import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useState } from 'react';
import type {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { uploadUserColumnPreferences } from 'api/user';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type { Column } from 'declarations/types/common';
import { useFetchUserColumnPreferences } from 'hooks/api/useUser';

import {
  SCHEDULE_EXCLUDED_COLUMN_IDS,
  TICKET_EXCLUDED_COLUMN_IDS,
  getColumnsForSection,
  getStorageKeyForSection,
} from './mockedColumns';

type ColumnPickerSettingsProps = {
  activeTab: ActiveTabTypeE;
  onSaveAndClose: () => void;
};

const COLUMN_LABEL_MIGRATION: Record<string, string> = {
  'RO number': 'RO',
  'Line number': 'Line',
  Priority: 'P',
  'Age (days)': 'Age',
  'Claim Age (days)': 'Claim Age',
  'Assigned to:': 'Assignee',
  'Advisor number': 'Advisor',
  'Tech number': 'Tech',
  'Account Number': 'Account',
};

const ColumnPickerSettings: FC<ColumnPickerSettingsProps> = ({
  activeTab,
  onSaveAndClose,
}): JSX.Element => {
  const columns = getColumnsForSection(activeTab);
  const storageKey = getStorageKeyForSection(activeTab);
  const isScheduleSection = activeTab === ActiveTabTypeE.Schedule;

  const userColumnPreferences = useFetchUserColumnPreferences({
    onSuccess: (data): void => {
      localStorage.setItem('visibleColumns', data);
    },
    onError: (err): void => {
      if (err?.data?.detail?.startsWith?.('The specified key does not exist.')) {
        const defaultCols = columns.map(c => ({ ...c, visible: true }));
        if (!isScheduleSection) {
          uploadUserColumnPreferences({ RawData: JSON.stringify(defaultCols) });
        }
      }
    },
  });

  const storedJSON =
    userColumnPreferences.data ??
    localStorage.getItem(storageKey) ??
    (isScheduleSection ? null : localStorage.getItem('visibleColumns'));
  const storedRaw = storedJSON ? JSON.parse(storedJSON) : columns;
  /** Merge stored columns with defaults: apply migration, then append any default columns missing from stored (e.g. Audit Trail) */
  const storedColumns: Column[] = ((): Column[] => {
    if (!Array.isArray(storedRaw) || storedRaw.length === 0) return columns;
    let migrated = (storedRaw as Column[]).map(col => ({
      ...col,
      label: COLUMN_LABEL_MIGRATION[col.label] ?? col.label,
    }));
    if (isScheduleSection) {
      migrated = migrated.filter(col => !SCHEDULE_EXCLUDED_COLUMN_IDS.includes(col.id));
    } else {
      migrated = migrated.filter(col => !TICKET_EXCLUDED_COLUMN_IDS.includes(col.id));
    }
    const storedIds = new Set(migrated.map((c): string => c.id));
    const missing = columns.filter(col => !storedIds.has(col.id));
    return missing.length > 0 ? [...migrated, ...missing] : migrated;
  })();

  const [visibleColumns, setVisibleColumns] = useState<Column[]>(storedColumns);

  const handleVisibilityToggle = (column: Column): void => {
    setVisibleColumns(prev =>
      prev.map(c => (c.id === column.id ? { ...c, visible: !c.visible } : c))
    );
  };

  const handleOrderChange = (result: DropResult): void => {
    if (!result.destination) return;
    const reordered = Array.from(visibleColumns);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setVisibleColumns(reordered);
  };

  const handleSaveConfig = (): void => {
    localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    if (!isScheduleSection) {
      uploadUserColumnPreferences({ RawData: JSON.stringify(visibleColumns) });
    }
    onSaveAndClose();
  };

  const handleResetConfig = (): void => {
    setVisibleColumns(columns);
  };

  const renderClone = (
    provided: DraggableProvided,
    _snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ): JSX.Element => {
    const column = visibleColumns[rubric.source.index];
    return (
      <Box
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        sx={{
          ...provided.draggableProps.style,
          padding: '4px 6px',
          border: (theme): string => `1px solid ${theme.palette.grey[400]}`,
          borderRadius: '8px',
          backgroundColor: (theme): string =>
            (theme.palette.background as { paper?: string })?.paper ??
            theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box display="flex" justifyContent="center">
          <DragIndicatorIcon sx={{ fontSize: '16px' }} />
        </Box>
        <Typography
          variant="caption"
          textAlign="center"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}
        >
          {column.label}
        </Typography>
        {!column.isRequired && (
          <Switch
            checked={column.visible}
            size="small"
            sx={{ cursor: 'pointer', mt: -0.5 }}
            onChange={(): void => handleVisibilityToggle(column)}
          />
        )}
      </Box>
    );
  };

  return (
    <Box py={2} px={4}>
      <Box
        m="5px 0px"
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <DragDropContext onDragEnd={handleOrderChange}>
          <Droppable droppableId="columns" direction="horizontal" renderClone={renderClone}>
            {(provided): JSX.Element => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ display: 'flex', gap: 0.5 }}
              >
                {visibleColumns.map((column, index) => (
                  <Draggable
                    key={`key-${column.id}`}
                    draggableId={`drag-${column.id}`}
                    index={index}
                  >
                    {(provided, snapshot): JSX.Element => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          padding: '4px 6px',
                          border: snapshot.isDragging
                            ? 'none'
                            : (theme): string => `1px solid ${theme.palette.grey[400]}`,
                          borderRadius: '8px',
                          backgroundColor: snapshot.isDragging
                            ? (theme): string => theme.palette.grey[100]
                            : 'transparent',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Box display="flex" justifyContent="center">
                          <DragIndicatorIcon sx={{ fontSize: '16px' }} />
                        </Box>
                        <Typography
                          variant="caption"
                          textAlign="center"
                          sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {column.label}
                        </Typography>
                        {!column.isRequired ? (
                          <Switch
                            checked={column.visible}
                            size="small"
                            sx={{ cursor: 'pointer', mt: -0.5 }}
                            onChange={(): void => handleVisibilityToggle(column)}
                          />
                        ) : (
                          <Box sx={{ height: 28 }} />
                        )}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <Box mt={3} display="flex" justifyContent="flex-end" gap="10px">
        <Button onClick={handleResetConfig}>Reset</Button>
        <Button color="primary" variant="contained" onClick={handleSaveConfig}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default ColumnPickerSettings;
